import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from "@material-ui/core";
import { useTranslate } from "react-admin";
import { startOfDay } from "date-fns";
import InfoIcon from "@material-ui/icons/Info";
import { formatTooltipAxis } from "./chartUtils";
import { useFuelEconomyInMpg } from "../hooks/useFuelEconomyHooks";
import type { FuelEconomyHookParams } from "../hooks/useFuelEconomyHooks";
import { useProLogsTheme } from "../../../common/hooks/useProLogsTheme";
// eslint-disable-next-line @typescript-eslint/no-require-imports
import ReactECharts from "echarts-for-react";
import "./FuelEconomyReport.css";

type Props = FuelEconomyHookParams;

interface FuelEconomyChartPoint {
  label: string;
  value: number | null;
}

export const FuelEconomyGraphWidget: React.FC<Props> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const theme = useProLogsTheme();
  const { data, loading: isLoading, error } = useFuelEconomyInMpg({
    fromDate,
    toDate,
    periodType,
  });
  const isError = !!error;

  const mpgLabels = useMemo(() => {
    const result: string[] = [];
    const fromStart = startOfDay(fromDate);
    const toStart = startOfDay(toDate);
    const oneDay = 24 * 60 * 60 * 1000;

    for (let d = fromStart.getTime(); d <= toStart.getTime(); d += oneDay) {
      result.push(new Date(d).toLocaleDateString());
    }

    return result;
  }, [fromDate, toDate]);

  const mpgSeries = useMemo<FuelEconomyChartPoint[]>(() => {
    if (!data?.values || data.values.length === 0) {
      return [];
    }

    const values = data.values;

    return mpgLabels.map((label, idx) => ({
      label,
      value: idx < values.length ? values[idx] ?? null : null,
    }));
  }, [data, mpgLabels]);

  const chartOption = useMemo(
    () => ({
      tooltip: {
        trigger: "axis",
        formatter: formatTooltipAxis,
      },
      xAxis: {
        type: "category",
        data: mpgSeries.map((p) => p.label),
        axisLabel: {
          color: theme.palette.text.primary,
        },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          color: theme.palette.text.primary,
        },
      },
      series: [
        {
          name: translate("fuelEconomyReport.fuelEconomyInMpg", {
            _: "Fuel economy (MPG)",
          }),
          type: "bar",
          data: mpgSeries.map((p) => p.value ?? 0),
        },
      ],
    }),
    [mpgSeries, translate, theme.palette.text.primary],
  );

  const hasData = useMemo(() => {
    return mpgSeries.length > 0 && mpgSeries.some((p) => p.value != null);
  }, [mpgSeries]);

  return (
    <Card className="widget" style={{ height: "100%" }}>
      <div className="widget-title">
        <div className="widget-title-icon" style={{ color: "#1976d2" }}>
          <InfoIcon />
        </div>
        <div className="widget-title-label">
          {translate("fuelEconomyReport.fuelEconomyInMpg", {
            _: "Fuel economy (MPG)",
          })}
        </div>
      </div>
      <CardContent
        style={{
          padding: 16,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isLoading ? (
          <Grid
            container
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <CircularProgress />
          </Grid>
        ) : isError ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <Typography variant="body2" color="error">
              {translate("common.errorLoadingData", {
                _: "Error loading data",
              })}
            </Typography>
          </Box>
        ) : !hasData ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <Typography variant="body2" color="textSecondary">
              {translate("common.noData", {
                _: "No data",
              })}
            </Typography>
          </Box>
        ) : (
          <ReactECharts style={{ height: 320, flex: 1 }} option={chartOption} />
        )}
      </CardContent>
    </Card>
  );
};

export default FuelEconomyGraphWidget;
